// A digital humanoid worker drawn in code, feet at (0, 0), one unit = half a floor tile.
// Frames 0-7 animate the working loop: the hand on the screen, the visor scan, lines being written.
export const FRAMES = 8;
export const BOX = { left: -0.54, top: -2.8, width: 2.16, height: 3.0 };

const INK = "#191919";
const SHADE = "#ececec";

function roundRect(g, x, y, w, h, r) {
  if (g.roundRect) g.roundRect(x, y, w, h, r);
  else g.rect(x, y, w, h);
}

// An outlined capsule limb: a dark stroke with a lighter core.
function limb(g, points, width, line, solid, core = "#ffffff") {
  g.beginPath();
  g.moveTo(points[0][0], points[0][1]);
  for (let k = 1; k < points.length; k += 1) g.lineTo(points[k][0], points[k][1]);
  g.lineCap = "round";
  g.lineJoin = "round";
  g.strokeStyle = INK;
  g.lineWidth = width;
  g.stroke();
  if (solid) return;
  g.strokeStyle = core;
  g.lineWidth = Math.max(0.5, width - line * 2);
  g.stroke();
}

function joint(g, x, y, r, line, solid) {
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fillStyle = solid ? INK : SHADE;
  g.fill();
  if (solid) return;
  g.lineWidth = line;
  g.strokeStyle = INK;
  g.stroke();
}

export function drawHumanoid(g, u, frame = 0, { solid = false, screen: withScreen = true } = {}) {
  const phase = frame / FRAMES;
  const line = Math.max(0.8, u * 0.028);
  const bob = Math.sin(phase * Math.PI * 2) * 0.04 * u;
  const U = (v) => v * u;
  g.save();
  g.lineWidth = line;
  g.strokeStyle = INK;
  g.lineJoin = "round";
  if (!solid) {
    g.beginPath();
    g.ellipse(0, 0, U(0.46), U(0.15), 0, 0, Math.PI * 2);
    g.fillStyle = "rgba(25,25,25,0.1)";
    g.fill();
  }
  // Legs
  for (const side of [-1, 1]) {
    limb(g, [[U(0.1 * side), U(-1.08)], [U(0.115 * side), U(-0.6)]], U(0.16), line, solid, side > 0 ? SHADE : "#fff");
    limb(g, [[U(0.115 * side), U(-0.6)], [U(0.12 * side), U(-0.1)]], U(0.12), line, solid, side > 0 ? SHADE : "#fff");
    joint(g, U(0.115 * side), U(-0.6), U(0.055), line, solid);
    g.beginPath();
    g.ellipse(U(0.13 * side + 0.02), U(-0.045), U(0.1), U(0.048), 0, 0, Math.PI * 2);
    g.fillStyle = solid ? INK : "#fff";
    g.fill();
    if (!solid) g.stroke();
  }
  // Pelvis
  g.beginPath();
  g.moveTo(U(-0.2), U(-1.2));
  g.lineTo(U(0.2), U(-1.2));
  g.quadraticCurveTo(U(0.2), U(-1.04), U(0), U(-1.0));
  g.quadraticCurveTo(U(-0.2), U(-1.04), U(-0.2), U(-1.2));
  g.fillStyle = solid ? INK : SHADE;
  g.fill();
  if (!solid) g.stroke();
  // The floating screen it operates
  const screen = () => {
    g.moveTo(U(0.76), U(-1.78));
    g.lineTo(U(1.56), U(-2.03));
    g.lineTo(U(1.56), U(-2.74));
    g.lineTo(U(0.76), U(-2.49));
    g.closePath();
  };
  if (withScreen) {
    g.beginPath();
    screen();
    g.fillStyle = solid ? INK : "rgba(255,255,255,0.96)";
    g.fill();
  }
  if (!solid && withScreen) {
    g.lineWidth = line * 0.9;
    g.strokeStyle = "rgba(25,25,25,0.7)";
    g.stroke();
    const slope = -0.3125;
    const row = (x0, y0, length, alpha, width) => {
      g.beginPath();
      g.moveTo(U(x0), U(y0));
      g.lineTo(U(x0 + length), U(y0 + length * slope));
      g.strokeStyle = `rgba(25,25,25,${alpha})`;
      g.lineWidth = U(width);
      g.lineCap = "round";
      g.stroke();
    };
    row(0.86, -2.4, 0.3, 0.8, 0.035);
    row(0.86, -2.29, 0.58, 0.28, 0.022);
    row(0.86, -2.2, 0.46, 0.28, 0.022);
    row(0.86, -2.11, (0.12 + 0.46 * phase), 0.5, 0.022);
    for (let b = 0; b < 5; b += 1) {
      const hgt = 0.05 + ((b * 37 + frame * (b + 1) * 13) % 9) * 0.012;
      const x = 1.08 + b * 0.075;
      const base = -1.9 + (x - 0.86) * slope;
      g.beginPath();
      g.moveTo(U(x), U(base));
      g.lineTo(U(x), U(base - hgt));
      g.strokeStyle = b === 4 ? INK : "rgba(25,25,25,0.35)";
      g.lineWidth = U(0.035);
      g.lineCap = "butt";
      g.stroke();
    }
    g.lineCap = "round";
    g.lineWidth = line;
    g.strokeStyle = INK;
  }
  // Left arm, relaxed
  joint(g, U(-0.31), U(-1.88), U(0.075), line, solid);
  limb(g, [[U(-0.33), U(-1.84)], [U(-0.37), U(-1.5)]], U(0.12), line, solid);
  limb(g, [[U(-0.37), U(-1.5)], [U(-0.35), U(-1.16)]], U(0.1), line, solid);
  joint(g, U(-0.37), U(-1.5), U(0.045), line, solid);
  // Torso
  const torso = () => {
    g.moveTo(U(-0.3), U(-1.97));
    g.quadraticCurveTo(U(-0.29), U(-1.72), U(-0.24), U(-1.62));
    g.quadraticCurveTo(U(-0.17), U(-1.42), U(-0.17), U(-1.3));
    g.lineTo(U(-0.19), U(-1.16));
    g.lineTo(U(0.19), U(-1.16));
    g.lineTo(U(0.17), U(-1.3));
    g.quadraticCurveTo(U(0.17), U(-1.42), U(0.24), U(-1.62));
    g.quadraticCurveTo(U(0.29), U(-1.72), U(0.3), U(-1.97));
    g.quadraticCurveTo(U(0), U(-2.05), U(-0.3), U(-1.97));
    g.closePath();
  };
  g.beginPath();
  torso();
  g.fillStyle = solid ? INK : "#fff";
  g.fill();
  if (!solid) {
    g.save();
    g.clip();
    g.fillStyle = SHADE;
    g.fillRect(U(0.1), U(-2.1), U(0.3), U(1));
    g.restore();
    g.beginPath();
    torso();
    g.lineWidth = line;
    g.strokeStyle = INK;
    g.stroke();
    g.beginPath();
    g.moveTo(U(-0.2), U(-1.8));
    g.quadraticCurveTo(U(0), U(-1.64), U(0.2), U(-1.8));
    g.moveTo(U(-0.1), U(-1.44));
    g.lineTo(U(0.1), U(-1.44));
    g.moveTo(U(-0.08), U(-1.36));
    g.lineTo(U(0.08), U(-1.36));
    g.strokeStyle = "rgba(25,25,25,0.3)";
    g.lineWidth = line * 0.8;
    g.stroke();
    g.beginPath();
    g.arc(U(0.11), U(-1.88), U(0.03), 0, Math.PI * 2);
    g.fillStyle = frame % 4 < 2 ? INK : "rgba(25,25,25,0.18)";
    g.fill();
  }
  // Neck and helmet
  g.beginPath();
  roundRect(g, U(-0.055), U(-2.13), U(0.11), U(0.14), U(0.03));
  g.fillStyle = solid ? INK : SHADE;
  g.fill();
  if (!solid) {
    g.lineWidth = line;
    g.stroke();
  }
  g.beginPath();
  g.ellipse(U(0.01), U(-2.34), U(0.2), U(0.235), 0, 0, Math.PI * 2);
  g.fillStyle = solid ? INK : "#fff";
  g.fill();
  if (!solid) {
    g.save();
    g.clip();
    g.fillStyle = SHADE;
    g.fillRect(U(0.08), U(-2.6), U(0.2), U(0.5));
    g.restore();
    g.beginPath();
    g.ellipse(U(0.01), U(-2.34), U(0.2), U(0.235), 0, 0, Math.PI * 2);
    g.lineWidth = line;
    g.stroke();
    g.beginPath();
    g.arc(U(-0.17), U(-2.31), U(0.04), 0, Math.PI * 2);
    g.fillStyle = SHADE;
    g.fill();
    g.stroke();
    g.beginPath();
    roundRect(g, U(-0.15), U(-2.42), U(0.36), U(0.13), U(0.065));
    g.fillStyle = INK;
    g.fill();
    g.beginPath();
    g.moveTo(U(-0.1), U(-2.405));
    g.lineTo(U(0.15), U(-2.405));
    g.strokeStyle = "rgba(255,255,255,0.35)";
    g.lineWidth = Math.max(0.6, U(0.012));
    g.stroke();
    g.beginPath();
    roundRect(g, U(-0.12 + 0.24 * phase), U(-2.385), U(0.07), U(0.06), U(0.03));
    g.fillStyle = "rgba(255,255,255,0.92)";
    g.fill();
  }
  // Right arm, working the screen
  joint(g, U(0.31), U(-1.88), U(0.075), line, solid);
  limb(g, [[U(0.33), U(-1.84)], [U(0.47), U(-1.55)]], U(0.12), line, solid, SHADE);
  limb(g, [[U(0.47), U(-1.55)], [U(0.7), U(-1.74) + bob]], U(0.1), line, solid, SHADE);
  joint(g, U(0.47), U(-1.55), U(0.045), line, solid);
  g.beginPath();
  g.arc(U(0.72), U(-1.76) + bob, U(0.05), 0, Math.PI * 2);
  g.fillStyle = solid ? INK : "#fff";
  g.fill();
  if (!solid) {
    g.lineWidth = line;
    g.stroke();
  }
  g.restore();
}

// Renders the working loop once so thousands of workers can be drawn as images.
export function humanoidSprites(u, dpr = 1, options = {}) {
  const scale = u * dpr;
  return Array.from({ length: FRAMES }, (_, frame) => {
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(BOX.width * scale);
    canvas.height = Math.ceil(BOX.height * scale);
    const g = canvas.getContext("2d");
    g.translate(-BOX.left * scale, -BOX.top * scale);
    drawHumanoid(g, scale, frame, options);
    return canvas;
  });
}

// Points on the humanoid's silhouette, centred on its chest, for particles to gather into.
export function humanoidPoints(u, gap) {
  const canvas = document.createElement("canvas");
  const w = Math.ceil(BOX.width * u);
  const h = Math.ceil(BOX.height * u);
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext("2d");
  g.translate(-BOX.left * u, -BOX.top * u);
  drawHumanoid(g, u, 2, { solid: true });
  const data = g.getImageData(0, 0, w, h).data;
  const points = [];
  for (let y = 0; y < h; y += gap)
    for (let x = 0; x < w; x += gap)
      if (data[(Math.floor(y) * w + Math.floor(x)) * 4 + 3] > 120)
        points.push([x + BOX.left * u - 0.3 * u, y + BOX.top * u + 1.4 * u]);
  return points;
}

// A humanoid walking in profile, facing right, feet at (0, 0). Phase 0-1 is one full stride.
// "human" draws the leader: a solid figure without a visor. carry: "tablet" | "phone" | null.
export function drawWalker(g, u, phase, { human = false, carry = null } = {}) {
  const U = (v) => v * u;
  const a = phase * Math.PI * 2;
  const line = Math.max(0.8, u * 0.03);
  const bob = -Math.abs(Math.sin(a)) * U(0.05);
  const body = human ? INK : "#ffffff";
  const far = human ? "#3a3a3a" : SHADE;
  g.save();
  g.lineCap = "round";
  g.lineJoin = "round";
  g.beginPath();
  g.ellipse(0, 0, U(0.34), U(0.08), 0, 0, Math.PI * 2);
  g.fillStyle = "rgba(25,25,25,0.12)";
  g.fill();
  g.translate(0, bob);
  const hip = [U(0), U(-1.08)];
  const leg = (phi, core) => {
    const thigh = Math.sin(phi) * 0.42;
    const bend = 0.08 + Math.max(0, Math.cos(phi)) * 0.62;
    const knee = [hip[0] + Math.sin(thigh) * U(0.5), hip[1] + Math.cos(thigh) * U(0.5)];
    const shin = thigh - bend;
    const foot = [knee[0] + Math.sin(shin) * U(0.5), knee[1] + Math.cos(shin) * U(0.5)];
    limb(g, [hip, knee], U(0.15), line, false, core);
    limb(g, [knee, foot], U(0.12), line, false, core);
    g.beginPath();
    g.ellipse(foot[0] + U(0.05), foot[1] - U(0.02), U(0.1), U(0.045), 0, 0, Math.PI * 2);
    g.fillStyle = core;
    g.fill();
    g.strokeStyle = INK;
    g.lineWidth = line;
    g.stroke();
  };
  const shoulder = [U(0.03), U(-1.86)];
  const arm = (swing, core, pose) => {
    let elbow;
    let hand;
    if (pose === "phone") {
      elbow = [shoulder[0] + U(0.2), shoulder[1] + U(0.22)];
      hand = [U(0.12), U(-2.22)];
    } else if (pose === "tablet") {
      elbow = [shoulder[0] + U(0.06), shoulder[1] + U(0.34)];
      hand = [shoulder[0] + U(0.34), shoulder[1] + U(0.3)];
    } else {
      elbow = [shoulder[0] + Math.sin(swing) * U(0.34), shoulder[1] + Math.cos(swing) * U(0.34)];
      const fore = swing + 0.35 + Math.max(0, swing) * 0.6;
      hand = [elbow[0] + Math.sin(fore) * U(0.32), elbow[1] + Math.cos(fore) * U(0.32)];
    }
    limb(g, [shoulder, elbow], U(0.11), line, false, core);
    limb(g, [elbow, hand], U(0.095), line, false, core);
    return hand;
  };
  arm(Math.sin(a) * 0.5, far, carry === "tablet" ? "tablet" : null);
  leg(a + Math.PI, far);
  leg(a, body);
  // Torso, leaning into the walk
  g.save();
  g.translate(U(0.01), U(-1.47));
  g.rotate(0.06);
  g.beginPath();
  roundRect(g, U(-0.17), U(-0.48), U(0.34), U(0.84), U(0.14));
  g.fillStyle = body;
  g.fill();
  g.strokeStyle = INK;
  g.lineWidth = line;
  g.stroke();
  if (!human) {
    g.beginPath();
    g.arc(U(0.08), U(-0.28), U(0.03), 0, Math.PI * 2);
    g.fillStyle = INK;
    g.fill();
  }
  g.restore();
  // Head
  const c = [U(0.06), U(-2.3)];
  g.beginPath();
  g.ellipse(c[0], c[1], U(0.19), U(0.225), 0, 0, Math.PI * 2);
  g.fillStyle = body;
  g.fill();
  if (!human) {
    g.save();
    g.clip();
    g.fillStyle = INK;
    g.fillRect(c[0] + U(0.02), c[1] - U(0.1), U(0.2), U(0.13));
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.fillRect(c[0] + U(0.06 + 0.08 * phase), c[1] - U(0.08), U(0.04), U(0.06));
    g.restore();
  }
  g.beginPath();
  g.ellipse(c[0], c[1], U(0.19), U(0.225), 0, 0, Math.PI * 2);
  g.strokeStyle = INK;
  g.lineWidth = line;
  g.stroke();
  if (!human) {
    g.beginPath();
    g.moveTo(c[0] - U(0.1), c[1] - U(0.17));
    g.quadraticCurveTo(c[0] - U(0.2), c[1], c[0] - U(0.1), c[1] + U(0.17));
    g.strokeStyle = "rgba(25,25,25,0.3)";
    g.lineWidth = line * 0.8;
    g.stroke();
  }
  const hand = arm(Math.sin(a + Math.PI) * 0.5, body, carry);
  if (carry === "phone") {
    g.save();
    g.translate(hand[0], hand[1]);
    g.rotate(0.25);
    g.fillStyle = INK;
    g.beginPath();
    roundRect(g, U(-0.03), U(-0.09), U(0.06), U(0.16), U(0.015));
    g.fill();
    g.restore();
  } else if (carry === "tablet") {
    g.save();
    g.translate(hand[0] + U(0.02), hand[1] - U(0.06));
    g.rotate(-0.5);
    g.fillStyle = INK;
    g.beginPath();
    roundRect(g, U(-0.03), U(-0.17), U(0.06), U(0.3), U(0.02));
    g.fill();
    g.restore();
  }
  g.restore();
}

export const WALK_FRAMES = 8;
export const WALK_BOX = { left: -0.6, top: -2.62, width: 1.2, height: 2.8 };

export function walkerSprites(u, dpr, options) {
  const scale = u * dpr;
  return Array.from({ length: WALK_FRAMES }, (_, frame) => {
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(WALK_BOX.width * scale);
    canvas.height = Math.ceil(WALK_BOX.height * scale);
    const g = canvas.getContext("2d");
    g.translate(-WALK_BOX.left * scale, -WALK_BOX.top * scale);
    drawWalker(g, scale, frame / WALK_FRAMES, options);
    return canvas;
  });
}
